"use client";

import Image from "next/image";
import Link from "next/link";
import { Clock } from "lucide-react";
import { motion } from "framer-motion";
import type { Assessment } from "@/features/assessment/types";
import type { AppLocale } from "@/core/i18n/config";
import { localizeHref } from "@/core/i18n/routing";

interface AssessmentFeaturedCardProps {
  assessment: Assessment;
  locale: AppLocale;
  index: number;
}

export function AssessmentFeaturedCard({ assessment, locale, index }: AssessmentFeaturedCardProps) {
  const href = localizeHref(locale, `/assessment/${assessment.slug}`);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="group flex flex-col justify-between rounded-2xl border border-border bg-card p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
    >
      {/* Top: text content */}
      <div className="mb-6">
        <span className="mb-3 inline-block text-[10px] font-bold tracking-[0.18em] text-primary uppercase">
          {assessment.category}
        </span>
        <h3 className="mb-3 font-heading text-xl leading-snug font-semibold text-foreground transition-colors duration-300 group-hover:text-primary">
          {assessment.title}
        </h3>
        <p className="text-sm leading-relaxed text-muted-foreground">{assessment.description}</p>
      </div>

      {/* Bottom: image + meta + CTA */}
      <div>
        {/* Image */}
        <div className="relative mb-4 h-40 w-full overflow-hidden rounded-xl bg-muted">
          <Image
            src={assessment.imageUrl}
            alt={assessment.title}
            fill
            sizes="(max-width: 768px) 100vw, 400px"
            className="object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>

        {/* Duration + CTA row */}
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock className="h-3.5 w-3.5" />
            {assessment.durationLabel}
          </span>
          <Link
            href={href}
            className="text-xs font-bold tracking-[0.12em] text-primary uppercase transition-colors duration-200 hover:text-primary/80"
          >
            Take Assessment
          </Link>
        </div>
      </div>
    </motion.div>
  );
}
